import type { DayTradingStreamInfo } from '@/hooks/useDayTradingWebSocket';
import type { DayTradingSessionID } from '@/models/ids';
import { useYouTubeLiveStatus } from '@/hooks/useYouTubeLiveStatus';
import { useDirectStreamStatus } from '@/hooks/useDirectStreamStatus';

/**
 * Unified stream status across all modes:
 *  - YOUTUBE/DIRECT poll their respective backends
 *  - GOOGLE_MEET/DISCORD/NONE always return 'offline' (no live signal is observable)
 *
 * Polling is gated on `mode`, so we don't hammer the YT/MediaMTX APIs when
 * the session is in a mode that doesn't need them.
 */
export type StreamStatus = 'live' | 'vod' | 'offline' | 'error' | 'unknown';

export function useStreamStatus(
  sessionId: DayTradingSessionID,
  streamInfo: DayTradingStreamInfo,
  enabled: boolean,
): StreamStatus {
  const { status: youtubeStatus } = useYouTubeLiveStatus(
    streamInfo.mode === 'YOUTUBE' ? streamInfo.youtubeURL : '',
  );
  const { status: directStatus } = useDirectStreamStatus(
    sessionId,
    enabled && streamInfo.mode === 'DIRECT',
  );

  switch (streamInfo.mode) {
    case 'YOUTUBE':     return youtubeStatus;
    case 'DIRECT':      return directStatus;
    case 'GOOGLE_MEET':
    case 'DISCORD':
    case 'NONE':
      return 'offline';
  }
}
